import React from "react";

import Typography from "@mui/material/Typography";

import AttendanceIndicator from "@app/analytics/components/AttendanceIndicator";
import TableCell from "@app/analytics/components/Table/TableCell";
import type { UserMeeting } from "@app/common/types";
import { formatStartAndEndTime } from "@app/common/utils";

import RecurringMeetingBadge from "@rui/RecurringMeetingBadge";
import Stack, { Row } from "@rui/Stack";

const DateAndTimeCell = ({
  isNested = false,
  isSummary = false,
  report,
  showAttendance = false,
}: {
  isNested?: boolean;
  isSummary?: boolean;
  report: UserMeeting;
  showAttendance?: boolean;
}) => {
  // use the scheduled start time if available for all meeting types, provided
  // the scheduled start time is before the observed end time (i.e., account for
  // cases where someone joined early, left before the call started, and the bot
  // left thinking the meeting was over before it had technically begun)
  const useScheduledStartTime =
    report.scheduledStartTime != null &&
    (!report.endTime || report.scheduledStartTime < report.endTime);

  const startTimeToDisplay = useScheduledStartTime
    ? report.scheduledStartTime
    : report.startTime;

  // only summary rows should use the scheduled end time, since individual
  // sessions could have ended early or ran long
  const endTimeToDisplay = isSummary ? report.scheduledEndTime : report.endTime;

  const numAdditionalMeetingInstances = (report.instances ?? []).length - 1;

  return (
    <TableCell sx={{ color: "shades.neutral.700" }}>
      <Stack spacing={0.5}>
        <Row variant="left" spacing={0.5}>
          <Typography variant="bodySecondaryReg">
            {startTimeToDisplay?.toLocaleString({
              weekday: "short",
              month: "short",
              day: "numeric",
              year: "numeric",
            })}
            {numAdditionalMeetingInstances > 0 &&
              ` +${numAdditionalMeetingInstances}`}
          </Typography>
          {!isNested && report.seriesId && (
            <RecurringMeetingBadge size="10px" />
          )}
        </Row>
        <Row variant="left" spacing={1.5}>
          <Row spacing={0.5} variant="left">
            <Typography variant="labelReg" sx={{ color: "shades.neutral.500" }}>
              {formatStartAndEndTime(startTimeToDisplay, endTimeToDisplay)}
            </Typography>
            {showAttendance && (
              <AttendanceIndicator meeting={report} showTooltip />
            )}
          </Row>
        </Row>
      </Stack>
    </TableCell>
  );
};

export default DateAndTimeCell;
